import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import {type SxProps, type Theme} from "@mui/material/styles";
import {SIDEBAR_WIDTH} from "crqlar-react-common/dist/utils/useDimensions";
import {
    Children,
    createContext,
    Fragment,
    isValidElement,
    type ReactElement,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from "react";
import {useTranslation} from "react-i18next";
import {useCompactDashboardLayout} from "../../../utils/responsive.util";

const RAIL_ACTION_ATTRIBUTE = "data-right-sidebar-rail-action";

interface RightSidebarRailAction {
    id: string;
    icon: ReactNode;
    label: string;
    role?: "search";
    active?: boolean;
    disabled?: boolean;
    onSelect: () => void;
}

interface RightSidebarRailContextValue {
    actions: RightSidebarRailAction[];
    openId?: string;
    isMobile: boolean;
    toggle: (id: string) => void;
    close: () => void;
    registerAction: (
        id: string,
        getAction: () => RightSidebarRailAction,
    ) => () => void;
    refreshActions: () => void;
}

interface RightSidebarRailLayoutProps {
    children: ReactNode;
    sx?: SxProps<Theme>;
}

interface RightSidebarRailHeaderProps {
    children?: ReactNode;
    compact?: boolean;
}

interface RightSidebarFullscreenExitButtonProps {
    active: boolean;
    onExit: () => void;
}

interface RightSidebarRailProps {
    children: ReactNode;
}

interface RightSidebarPanelButtonProps {
    icon: ReactNode;
    label: string;
    disabled?: boolean;
    renderPanel: (close: () => void) => ReactNode;
}

interface RightSidebarActionButtonProps {
    icon: ReactNode;
    label: string;
    active?: boolean;
    disabled?: boolean;
    variant?: "icon" | "create";
    onClick: () => void;
}

interface RightSidebarCreateButtonProps {
    label: string;
    disabled?: boolean;
    onClick: () => void;
}

interface FlattenedRailChild {
    node: ReactNode;
    sourceIndex: number;
}

const RightSidebarRailContext = createContext<RightSidebarRailContextValue | null>(null);

const useRightSidebarRail = () => {
    const context = useContext(RightSidebarRailContext);
    if (!context) {
        throw new Error("Rail controls must be rendered inside RightSidebarRailLayout.");
    }

    return context;
};

const useRegisterRailAction = (action: RightSidebarRailAction) => {
    const {registerAction, refreshActions} = useRightSidebarRail();
    const actionRef = useRef(action);
    actionRef.current = action;

    useEffect(() => registerAction(action.id, () => actionRef.current), [action.id, registerAction]);

    useEffect(() => {
        refreshActions();
    }, [
        action.active,
        action.disabled,
        action.id,
        action.label,
        action.role,
        refreshActions,
    ]);
};

const getRegisteredActions = (
    actionIds: string[],
    actionGetters: Map<string, () => RightSidebarRailAction>,
) => actionIds
    .map<RightSidebarRailAction | undefined>(id => {
        const getAction = actionGetters.get(id);
        const action = getAction?.();
        if (!action) return undefined;

        const getCurrentAction = () => actionGetters.get(id)?.() ?? action;

        return {
            id,
            get icon() {
                return getCurrentAction().icon;
            },
            get label() {
                return getCurrentAction().label;
            },
            get role() {
                return getCurrentAction().role;
            },
            get active() {
                return getCurrentAction().active;
            },
            get disabled() {
                return getCurrentAction().disabled;
            },
            onSelect: () => getCurrentAction().onSelect(),
        };
    })
    .filter((action): action is RightSidebarRailAction => Boolean(action));

const flattenRailChildren = (
    children: ReactNode,
    flattened: FlattenedRailChild[] = [],
) => {
    Children.toArray(children).forEach(node => {
        if (isValidElement<{children?: ReactNode}>(node) && node.type === Fragment) {
            flattenRailChildren(node.props.children, flattened);
            return;
        }

        flattened.push({node, sourceIndex: flattened.length});
    });

    return flattened;
};

function RightSidebarRailProvider({children}: {children: ReactNode}) {
    const isMobile = useCompactDashboardLayout();
    const actionGettersRef = useRef(new Map<string, () => RightSidebarRailAction>());
    const [actions, setActions] = useState<RightSidebarRailAction[]>([]);
    const [openId, setOpenId] = useState<string>();

    const toggle = useCallback((id: string) => {
        setOpenId(current => current === id ? undefined : id);
    }, []);
    const close = useCallback(() => setOpenId(undefined), []);

    useEffect(() => {
        close();
    }, [close, isMobile]);

    const refreshActions = useCallback(() => {
        setActions(getRegisteredActions(
            Array.from(actionGettersRef.current.keys()),
            actionGettersRef.current,
        ));
    }, []);
    const registerAction = useCallback((
        id: string,
        getAction: () => RightSidebarRailAction,
    ) => {
        actionGettersRef.current.set(id, getAction);
        refreshActions();

        return () => {
            actionGettersRef.current.delete(id);
            refreshActions();
            setOpenId(current => current === id ? undefined : current);
        };
    }, [refreshActions]);

    const contextValue = useMemo<RightSidebarRailContextValue>(() => ({
        actions,
        openId,
        isMobile,
        toggle,
        close,
        registerAction,
        refreshActions,
    }), [actions, close, isMobile, openId, refreshActions, registerAction, toggle]);

    return (
        <RightSidebarRailContext.Provider value={contextValue}>
            {children}
        </RightSidebarRailContext.Provider>
    );
}

function RightSidebarRailMobileActions() {
    const {t} = useTranslation();
    const {actions, isMobile} = useRightSidebarRail();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const buttonId = useId();
    const menuId = useId();
    const searchActions = actions.filter(action => action.role === "search");
    const menuActions = actions.filter(action => action.role !== "search");
    const hasActiveMenuAction = menuActions.some(action => action.active);

    useEffect(() => {
        if (!isMobile || menuActions.length === 0) {
            setAnchorEl(null);
        }
    }, [isMobile, menuActions.length]);

    if (!isMobile || actions.length === 0) return null;

    return (
        <Stack
            direction="row"
            alignItems="center"
            gap={0.5}
            sx={{flex: "0 0 auto", minWidth: 0}}
        >
            {searchActions.map(action => (
                <Tooltip key={action.id} title={action.label}>
                    <Box component="span" sx={{display: "inline-flex"}}>
                        <IconButton
                            data-right-sidebar-rail-action
                            data-dashboard-control=""
                            type="button"
                            color="primary"
                            aria-label={action.label}
                            aria-pressed={!!action.active}
                            data-active={action.active || undefined}
                            disabled={action.disabled}
                            onClick={action.onSelect}
                            sx={{
                                width: 36,
                                height: 36,
                                flex: "0 0 36px",
                                borderRadius: 1,
                            }}
                        >
                            {action.icon}
                        </IconButton>
                    </Box>
                </Tooltip>
            ))}

            {menuActions.length > 0 && (
                <>
                    <Tooltip title={t("More")}>
                        <IconButton
                            id={buttonId}
                            type="button"
                            color="primary"
                            data-dashboard-control=""
                            data-active={hasActiveMenuAction || !!anchorEl || undefined}
                            aria-label={t("More")}
                            aria-haspopup="menu"
                            aria-expanded={!!anchorEl}
                            aria-controls={anchorEl ? menuId : undefined}
                            aria-pressed={hasActiveMenuAction}
                            onClick={event => setAnchorEl(event.currentTarget)}
                            sx={{
                                width: 36,
                                height: 36,
                                flex: "0 0 36px",
                                borderRadius: 1,
                            }}
                        >
                            <MoreVertIcon/>
                        </IconButton>
                    </Tooltip>

                    <Menu
                        id={menuId}
                        anchorEl={anchorEl}
                        open={!!anchorEl}
                        onClose={() => setAnchorEl(null)}
                        anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                        transformOrigin={{vertical: "top", horizontal: "right"}}
                        MenuListProps={{
                            "aria-labelledby": buttonId,
                        }}
                    >
                        {menuActions.map(action => (
                            <MenuItem
                                key={action.id}
                                data-right-sidebar-rail-action
                                selected={!!action.active}
                                disabled={action.disabled}
                                onClick={() => {
                                    setAnchorEl(null);
                                    action.onSelect();
                                }}
                            >
                                <ListItemIcon sx={{color: 'primary.main'}}>{action.icon}</ListItemIcon>
                                <ListItemText primary={action.label}/>
                            </MenuItem>
                        ))}
                    </Menu>
                </>
            )}
        </Stack>
    );
}

export function RightSidebarRailHeader({children, compact = false}: RightSidebarRailHeaderProps) {
    const {isMobile} = useRightSidebarRail();
    const hasContent = Children.count(children) > 0;

    return (
        <Box
            component="header"
            sx={{
                position: "relative",
                display: isMobile || hasContent ? "flex" : "none",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                minWidth: 0,
                minHeight: isMobile ? 48 : compact ? 64 : 92,
                flexShrink: 0,
                gap: 1,
                px: isMobile ? 1 : 2,
                boxSizing: "border-box",
            }}
        >
            {hasContent && (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: isMobile ? "flex-start" : "center",
                        flex: "1 1 auto",
                        minWidth: 0,
                        width: "100%",
                    }}
                >
                    {children}
                </Box>
            )}

            <RightSidebarRailMobileActions/>
        </Box>
    );
}

export function RightSidebarRailHeaderCenter({children}: {children: ReactNode}) {
    const {isMobile} = useRightSidebarRail();

    return (
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                minWidth: 0,
                maxWidth: "100%",
                transform: isMobile
                    ? "none"
                    : "translateX(calc(var(--right-sidebar-rail-width, 0px) / 2))",
            }}
        >
            {children}
        </Box>
    );
}

export function RightSidebarFullscreenExitButton({
    active,
    onExit,
}: RightSidebarFullscreenExitButtonProps) {
    const {t} = useTranslation();
    const isCompactDashboardLayout = useCompactDashboardLayout();

    if (!active || isCompactDashboardLayout) return null;

    return (
        <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={onExit}
            sx={{
                position: "absolute",
                top: "50%",
                left: theme => theme.spacing(2),
                zIndex: 1,
                transform: "translateY(-50%)",
                height: 36,
                px: 1.5,
                whiteSpace: "nowrap",
                bgcolor: "background.paper",
                boxShadow: 1,
            }}
        >
            {t("Exit full screen")} (Esc)
        </Button>
    );
}

export function RightSidebarRailLayout({children, sx}: RightSidebarRailLayoutProps) {
    let header: ReactElement<RightSidebarRailHeaderProps> | undefined;
    let rail: ReactElement<RightSidebarRailProps> | undefined;
    const content: FlattenedRailChild[] = [];

    flattenRailChildren(children).forEach(child => {
        if (isValidElement<RightSidebarRailHeaderProps>(child.node)
            && child.node.type === RightSidebarRailHeader) {
            header = child.node;
            return;
        }

        if (isValidElement<RightSidebarRailProps>(child.node)
            && child.node.type === RightSidebarRail) {
            rail = child.node;
            return;
        }

        content.push(child);
    });

    const additionalSx = sx
        ? (Array.isArray(sx) ? sx : [sx])
        : [];

    return (
        <RightSidebarRailProvider>
            <Box
                sx={[
                    {
                        "--right-sidebar-rail-width": `${rail ? SIDEBAR_WIDTH : 0}px`,
                        display: "flex",
                        flex: 1,
                        width: "100%",
                        minWidth: 0,
                        minHeight: 0,
                        position: "relative",
                    },
                    ...additionalSx,
                ]}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        minWidth: 0,
                        minHeight: 0,
                    }}
                >
                    {header ?? <RightSidebarRailHeader/>}

                    {content.map(child => (
                        <Fragment key={child.sourceIndex}>{child.node}</Fragment>
                    ))}
                </Box>

                {rail}
            </Box>
        </RightSidebarRailProvider>
    );
}

export function RightSidebarRail({children}: RightSidebarRailProps) {
    const {isMobile} = useRightSidebarRail();

    return (
        <Box
            component="aside"
            className="hide-on-print"
            sx={{
                flex: isMobile ? "0 0 0" : `0 0 ${SIDEBAR_WIDTH}px`,
                width: isMobile ? 0 : SIDEBAR_WIDTH,
                minWidth: isMobile ? 0 : SIDEBAR_WIDTH,
                position: isMobile ? "static" : "relative",
                boxSizing: "border-box",
                alignSelf: "stretch",
                borderLeft: isMobile ? 0 : "1px solid rgb(68 64 56 / 8%)",
                bgcolor: isMobile ? "transparent" : "background.paper",
            }}
        >
            <Stack
                alignItems="center"
                justifyContent="flex-start"
                gap={1.5}
                sx={{
                    height: "100%",
                    px: isMobile ? 0 : 0.5,
                    py: isMobile ? 0 : 1,
                    boxSizing: "border-box",
                }}
            >
                {children}
            </Stack>
        </Box>
    );
}

function RightSidebarPanelControl({
    role,
    icon,
    label,
    disabled = false,
    renderPanel,
}: RightSidebarPanelButtonProps & {role?: "search"}) {
    const id = useId();
    const {openId, isMobile, toggle, close} = useRightSidebarRail();
    const isOpen = openId === id;
    const active = isOpen && !disabled;
    const closeOnClickAway = (event: MouseEvent | TouchEvent) => {
        const target = event.target;
        if (target instanceof Element && target.closest(`[${RAIL_ACTION_ATTRIBUTE}]`)) return;

        close();
    };
    const panel = active ? (
        <ClickAwayListener
            mouseEvent="onMouseDown"
            touchEvent="onTouchEnd"
            onClickAway={closeOnClickAway}
        >
            <Box sx={{display: "contents"}}>
                {renderPanel(close)}
            </Box>
        </ClickAwayListener>
    ) : undefined;

    useEffect(() => {
        if (disabled && isOpen) close();
    }, [close, disabled, isOpen]);

    useRegisterRailAction({
        id,
        icon,
        label,
        role,
        active,
        disabled,
        onSelect: () => {
            if (!disabled) toggle(id);
        },
    });

    return (
        <>
            {active && panel}

            <Tooltip title={label} placement="bottom" arrow>
                <Box component="span" sx={{display: isMobile ? "none" : "inline-flex"}}>
                    <IconButton
                        data-right-sidebar-rail-action
                        data-dashboard-control=""
                        type="button"
                        color="primary"
                        data-active={active || undefined}
                        aria-label={label}
                        aria-pressed={active}
                        disabled={disabled}
                        onClick={() => toggle(id)}
                        sx={{borderRadius: 2}}
                    >
                        {icon}
                    </IconButton>
                </Box>
            </Tooltip>
        </>
    );
}

export function RightSidebarPanelButton(props: RightSidebarPanelButtonProps) {
    return <RightSidebarPanelControl {...props}/>;
}

export function RightSidebarSearchButton(props: RightSidebarPanelButtonProps) {
    return <RightSidebarPanelControl {...props} role="search"/>;
}

export function RightSidebarActionButton({
    icon,
    label,
    active = false,
    disabled = false,
    variant = "icon",
    onClick,
}: RightSidebarActionButtonProps) {
    const id = useId();
    const {isMobile, close} = useRightSidebarRail();
    const select = () => {
        if (disabled) return;

        close();
        onClick();
    };

    useRegisterRailAction({id, icon, label, active, disabled, onSelect: select});

    return (
        <Tooltip title={label} placement="bottom" arrow>
            <Box component="span" sx={{display: isMobile ? "none" : "inline-flex"}}>
                <IconButton
                    type="button"
                    color="primary"
                    data-dashboard-control=""
                    data-active={active || undefined}
                    aria-label={label}
                    aria-pressed={active}
                    disabled={disabled}
                    onClick={select}
                    sx={{
                        borderRadius: 2,
                        '&.Mui-disabled .RightSidebarCreateButton-visual': {
                            color: 'action.disabled',
                            backgroundColor: 'action.disabledBackground',
                        },
                    }}
                >
                    {variant === "create" ? (
                        <Box
                            component="span"
                            className="RightSidebarCreateButton-visual"
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 25,
                                height: 25,
                                flex: '0 0 25px',
                                borderRadius: '50%',
                                color: 'primary.contrastText',
                                backgroundColor: 'primary.main',
                            }}
                        >
                            {icon}
                        </Box>
                    ) : icon}
                </IconButton>
            </Box>
        </Tooltip>
    );
}

export function RightSidebarCreateButton({
    label,
    disabled = false,
    onClick,
}: RightSidebarCreateButtonProps) {
    return (
        <RightSidebarActionButton
            icon={<AddIcon/>}
            label={label}
            disabled={disabled}
            variant="create"
            onClick={onClick}
        />
    );
}

export function RightSidebarDivider() {
    const {isMobile} = useRightSidebarRail();

    return (
        <Divider
            sx={{
                display: isMobile ? "none" : "block",
                width: "90%",
                borderColor: "primary.main",
            }}
        />
    );
}
